import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Copy, Eye, EyeOff, Archive, Loader2, Briefcase, MapPin } from "lucide-react";
import { adminClient } from "@/lib/apiClient";
import {
  PROJECT_TYPE_LABEL,
  DIVISION_LABEL,
  PROJECT_LISTING_STATUS_LABEL,
} from "@/data/projectListingTemplates";
import AdminProjectListingModal from "@/components/admin/AdminProjectListingModal";

const STATUS_TONE = {
  open: "bg-emerald-100 text-emerald-700",
  "reviewing-partners": "bg-amber-100 text-amber-700",
  "walkthrough-scheduled": "bg-violet-100 text-violet-700",
  awarded: "bg-sky-100 text-sky-700",
  closed: "bg-gray-200 text-gray-600",
};

function StatusPill({ label, tone }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${tone}`}>
      {label}
    </span>
  );
}

export default function AdminProjectListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState({ open: false, mode: "create", initial: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminClient.get("/admin/project-listings");
      setListings(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load project listings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = () => setModal({ open: true, mode: "create", initial: null });
  const handleEdit = (l) => setModal({ open: true, mode: "edit", initial: l });
  const closeModal = () => setModal({ open: false, mode: "create", initial: null });

  const handleSave = async (payload) => {
    if (modal.mode === "edit" && modal.initial?.id) {
      await adminClient.patch(`/admin/project-listings/${modal.initial.id}`, payload);
    } else {
      await adminClient.post("/admin/project-listings", payload);
    }
    closeModal();
    await load();
  };

  const handleDelete = async (l) => {
    if (!window.confirm(`Delete "${l.title}"? Consider archiving instead.`)) return;
    try {
      await adminClient.delete(`/admin/project-listings/${l.id}`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const handleDuplicate = async (l) => {
    try {
      await adminClient.post(`/admin/project-listings/${l.id}/duplicate`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Duplicate failed.");
    }
  };

  const patch = async (l, body) => {
    try {
      await adminClient.patch(`/admin/project-listings/${l.id}`, body);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Update failed.");
    }
  };

  return (
    <div data-testid="admin-project-listings">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="br-eyebrow">Partner Opportunities</div>
          <h2 className="font-display text-2xl tracking-widest uppercase mt-1">Project Listings</h2>
          <p className="text-sm text-gray-600 mt-1">Create, edit, publish, archive, and duplicate scopes shown on /project-listings.</p>
        </div>
        <button type="button" onClick={handleCreate} className="br-btn-primary" data-testid="admin-projects-new">
          <Plus size={14} /> New Listing
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4" data-testid="admin-projects-error">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="admin-projects-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading project listings…
        </div>
      ) : listings.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center" data-testid="admin-projects-empty">
          <div className="w-12 h-12 mx-auto rounded-full bg-br-paper flex items-center justify-center text-br-bronze mb-3">
            <Briefcase size={20} />
          </div>
          <div className="font-display tracking-widest uppercase text-lg">No listings yet</div>
          <p className="mt-1 text-sm text-gray-600">Click “New Listing” — use the smart template button to auto-fill clean copy by project type + division.</p>
          <button type="button" onClick={handleCreate} className="br-btn-primary mt-4"><Plus size={14} /> New Listing</button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-br-paper border-b border-gray-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-gray-600">
                  <th className="px-4 py-3 font-bold">Project</th>
                  <th className="px-4 py-3 font-bold">Type / Division</th>
                  <th className="px-4 py-3 font-bold">Location</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listings.map((l) => (
                  <tr key={l.id} className="hover:bg-br-paper/30" data-testid={`admin-project-row-${l.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-gray-900">{l.title}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{l.start_date && `Starts ${l.start_date}`}{l.bid_due_date ? ` · Bid due ${l.bid_due_date}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">
                      <div>{PROJECT_TYPE_LABEL[l.project_type] || l.project_type}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{DIVISION_LABEL[l.division] || l.division}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">
                      <div className="flex items-center gap-1"><MapPin size={11} className="text-br-bronze" /> {l.city || l.service_area || "—"}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <StatusPill label={PROJECT_LISTING_STATUS_LABEL[l.status] || l.status} tone={STATUS_TONE[l.status] || STATUS_TONE.closed} />
                        {l.is_archived ? <StatusPill label="Archived" tone="bg-gray-200 text-gray-600" /> : l.is_published ? <StatusPill label="Published" tone="bg-emerald-100 text-emerald-700" /> : <StatusPill label="Draft" tone="bg-gray-200 text-gray-600" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <button type="button" onClick={() => patch(l, { is_published: !l.is_published })} className="p-1.5 text-gray-500 hover:text-br-bronze" aria-label="Toggle published" data-testid={`admin-project-toggle-pub-${l.id}`}>
                          {l.is_published ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button type="button" onClick={() => patch(l, { is_archived: !l.is_archived })} className="p-1.5 text-gray-500 hover:text-br-bronze" aria-label="Toggle archive" data-testid={`admin-project-toggle-arc-${l.id}`}>
                          <Archive size={14} />
                        </button>
                        <button type="button" onClick={() => handleDuplicate(l)} className="p-1.5 text-gray-500 hover:text-br-bronze" aria-label="Duplicate" data-testid={`admin-project-duplicate-${l.id}`}>
                          <Copy size={14} />
                        </button>
                        <button type="button" onClick={() => handleEdit(l)} className="p-1.5 text-gray-500 hover:text-br-bronze" aria-label="Edit" data-testid={`admin-project-edit-${l.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(l)} className="p-1.5 text-red-500 hover:text-red-700" aria-label="Delete" data-testid={`admin-project-delete-${l.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AdminProjectListingModal open={modal.open} mode={modal.mode} initial={modal.initial} onClose={closeModal} onSave={handleSave} />
    </div>
  );
}
